// src/components/VideoPopupContext.jsx
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const VideoPopupContext = createContext();

export const useVideoPopup = () => {
  const ctx = useContext(VideoPopupContext);
  if (!ctx) throw new Error('useVideoPopup must be used within a VideoPopupProvider');
  return ctx;
};

export const VideoPopupProvider = ({ children }) => {
  const [videoState, setVideoState] = useState(null); // { src, timestamp, youtubeLink, id, position }

  // Helpers
  const timestampToSeconds = (ts) => {
    if (!ts) return 0;
    const cleanTs = ts.toString().replace(/[^\d:]/g, ''); 
    return cleanTs.split(':').map(Number).reduce((acc, cur) => acc * 60 + cur, 0);
  };

  const extractVideoInfo = (url) => {
    if (!url) return null;
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (ytMatch) return { id: ytMatch[1], platform: 'youtube' };
    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
    if (vimeoMatch) return { id: vimeoMatch[1], platform: 'vimeo' };
    // Granicus (e.g. SFGovTV): MediaPlayer.php?view_id=10&clip_id=52945 or /player/clip/52945?view_id=10
    if (/granicus\.com/i.test(url)) {
      try {
        const u = new URL(url);
        const pathMatch = u.pathname.match(/\/player\/clip\/(\d+)/i);
        const clipId = u.searchParams.get('clip_id') || (pathMatch && pathMatch[1]);
        if (clipId) {
          return {
            id: clipId,
            platform: 'granicus',
            host: u.host,
            viewId: u.searchParams.get('view_id')
          };
        }
      } catch (e) { /* fall through */ }
    }
    return null;
  };

  // Internal helper to generate Embed URL
  const generateSrc = (videoInfo, startSeconds, autoplay = true) => {
      const autoVal = autoplay ? '1' : '0';
      if (videoInfo.platform === 'vimeo') {
        return `https://player.vimeo.com/video/${videoInfo.id}?autoplay=${autoVal}&controls=1#t=${startSeconds}s`;
      }
      if (videoInfo.platform === 'granicus') {
        const view = videoInfo.viewId ? `view_id=${videoInfo.viewId}&` : '';
        return `https://${videoInfo.host}/player/clip/${videoInfo.id}?${view}entrytime=${startSeconds}&autostart=${autoVal}&embed=1`;
      }
      return `https://www.youtube.com/embed/${videoInfo.id}?start=${startSeconds}&autoplay=${autoVal}&controls=1&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1&playsinline=1&loop=1`;
  };

  // Canonical watch URL with timestamp — used as the "Open externally" link
  // so users can fall back to the native player (Vimeo / YouTube) when the
  // embed is blocked (e.g. Vimeo videos restricted to specific domains).
  const buildExternalUrl = (videoInfo, originalUrl, startSeconds) => {
    if (!videoInfo) return originalUrl || '';
    const sec = Math.max(0, Math.floor(Number(startSeconds) || 0));
    if (videoInfo.platform === 'vimeo') {
      const base = `https://vimeo.com/${videoInfo.id}`;
      return sec > 0 ? `${base}#t=${sec}s` : base;
    }
    if (videoInfo.platform === 'youtube') {
      const base = `https://www.youtube.com/watch?v=${videoInfo.id}`;
      return sec > 0 ? `${base}&t=${sec}s` : base;
    }
    if (videoInfo.platform === 'granicus') {
      const params = [];
      if (videoInfo.viewId) params.push(`view_id=${videoInfo.viewId}`);
      if (sec > 0) params.push(`entrytime=${sec}`);
      const base = `https://${videoInfo.host}/player/clip/${videoInfo.id}`;
      return params.length ? `${base}?${params.join('&')}` : base;
    }
    return originalUrl || '';
  };

  // Called when clicking a specific timestamp -> Auto Play
  const showPopup = useCallback((timestamp, youtubeLink, targetElement) => {
    const videoInfo = extractVideoInfo(youtubeLink);
    const start = timestampToSeconds(timestamp);

    if (!videoInfo) return;

    let position = null;
    // Capture position if targetElement is provided
    if (targetElement && targetElement.getBoundingClientRect) {
      const rect = targetElement.getBoundingClientRect();
      position = { top: rect.top };
    }

    setVideoState({
      src: generateSrc(videoInfo, start, true),
      externalUrl: buildExternalUrl(videoInfo, youtubeLink, start),
      platform: videoInfo.platform,
      timestamp,
      youtubeLink,
      id: videoInfo.id,
      position
    });
  }, []);

  // Called when expanding a meeting -> Load Paused
  const loadVideo = useCallback((youtubeLink) => {
    const videoInfo = extractVideoInfo(youtubeLink);
    if (!videoInfo) return;

    setVideoState({
        src: generateSrc(videoInfo, 0, false),
        externalUrl: buildExternalUrl(videoInfo, youtubeLink, 0),
        platform: videoInfo.platform,
        timestamp: 'Start',
        youtubeLink,
        id: videoInfo.id,
        position: null
    });
  }, []);

  const closeVideo = useCallback(() => {
    setVideoState(null);
  }, []);

  const value = useMemo(() => ({
    videoState,
    showPopup,
    loadVideo,
    closeVideo
  }), [videoState, showPopup, loadVideo, closeVideo]);

  return (
    <VideoPopupContext.Provider value={value}>
      {children}
    </VideoPopupContext.Provider>
  );
};